import { useState, useEffect, useCallback } from 'react';
import { qnaApi } from '../services/qnaApi';

/**
 * InquiryStatusPage의 비즈니스 로직(이벤트 핸들러, 상태 관리)을 담당하는 커스텀 훅.
 */
export const useInquiryStatusPageEvents = (
    historyProps,
    sendMessage,
    fetchHistoryList,
    inputText,
    setInputText,
    isAiTyping,
    activeTab,
    CURRENT_USER,
    viewMode
) => {
    const [deleteTargetId, setDeleteTargetId] = useState(null);
    const [toastState, setToastState] = useState({ message: '', type: 'success' });

    const showToast = useCallback((message, type = 'success') => {
        setToastState({ message, type });
    }, []);

    const hideToast = useCallback(() => {
        setToastState(prev => ({ ...prev, message: '' }));
    }, []);

    // 1. 삭제 모달 열기
    const handleDeleteClick = (turnId) => {
        if (!turnId) return;
        setDeleteTargetId(turnId);
    };

    // 2. 진짜 삭제 수행
    const handleConfirmDelete = async () => {
        if (!deleteTargetId) return;
        try {
            const result = await qnaApi.historyDelete(deleteTargetId);
            if (result) {
                // 프론트엔드 상태에서 즉시 제거 (Optimistic Update)
                historyProps.removeHistoryItem(deleteTargetId);
                // 백엔드 데이터 최신화 (사이드바 등 동기화)
                fetchHistoryList(CURRENT_USER.id, viewMode);
                showToast('질의 내역이 안전하게 삭제되었습니다.', 'success');
            } else {
                showToast('삭제 처리에 실패했습니다.', 'error');
            }
        } catch (error) {
            // 실제 서버 에러 메시지를 콘솔에 출력하고 사용자에게 팝업으로 안내
            const serverMsg = error?.response?.data || error?.message;
            console.error('[삭제] 서버 에러 응답:', serverMsg);
            showToast(`삭제 처리 중 오류가 발생했습니다: ${serverMsg}`, 'error');
        } finally {
            setDeleteTargetId(null);
        }
    };

    // 3. 메시지 전송 핸들러
    const handleSend = async () => {
        if (!inputText.trim() || isAiTyping) return;
        const userQuestion = inputText;
        setInputText('');

        const aiResponse = await sendMessage(userQuestion);

        if (aiResponse) {
            const status = String(aiResponse.id ?? '').startsWith('error-') ? 'error' : 'success';
            historyProps.addHistory(userQuestion, aiResponse.text, status, aiResponse.id);
        }
    };

    // 탭 변경 시 페이징 초기화
    const { setCurrentPage } = historyProps;
    useEffect(() => {
        setCurrentPage(1);
    }, [activeTab, setCurrentPage]);

    return {
        deleteTargetId,
        setDeleteTargetId,
        toastState,
        setToastState,
        showToast,
        hideToast,
        handleDeleteClick,
        handleConfirmDelete,
        handleSend
    };
};
